import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { 
  Play, 
  Pause, 
  ChevronLeft, 
  ChevronRight, 
  Download, 
  Volume2, 
  VolumeX,
  Heart,
  MapPin,
  Calendar,
  Clock,
  BookOpen,
  Users,
  Feather,
  Music,
  MessageCircle,
  Send,
  User,
  Maximize,
  Minimize,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Memorial Data (hardcoded to ensure accuracy)
const memorialData = {
  name: 'Telma "TK" Linn',
  fullName: 'Telma Kenyatta Linn',
  dates: "Sunrise: January 14, 1975  —  Sunset: March 15, 2026",
  birthDate: "January 14, 1975",
  deathDate: "March 15, 2026",
  service: {
    date: "Saturday, March 28, 2026",
    time: "11:00 a.m.",
    location: "New Hope in Christ Ministries",
    address: "1708 Vera Cruz St, Memphis, TN 38117",
    attire: "Shades of Blue"
  },
  family: {
    husband: "Randall Linn",
    mother: "Delois \"Lois\" Kimmons Wilson (deceased)",
    siblings: [
      "Tonya Sharp (Michael)",
      "Sheila Allen (Samuel)",
      "Reginald 'Red' Fields",
      "Marcus Fields (Crystal)"
    ],
    aunts: [
      "Melissa (Terry) Robinson",
      "Tuesday Walton",
      "Tanarta Richmond"
    ],
    other: "A host of beloved nieces, nephews, and other family"
  },
  lifeReflection: `Telma Kenyatta "TK" Linn was born on January 14, 1975, to Delois "Lois" Kimmons Wilson. Raised by her mother and grandmother with a strong focus on education, TK grew into the smartest person many would ever know.

A woman of remarkable accomplishment, TK earned two degrees—one in drafting and another in architecture design. She found joy as a trainer at Jabil, where she loved helping others grow. But her greatest happiness came from her marriage to Randall, her high school sweetheart whom she met at a summer party. Their friendship was immediate, and she never changed from the honest, patient, loving woman he first knew. She was blessed to have a wonderful husband who truly loved her.

TK loved her church, New Hope in Christ Ministries. She was deeply artistic and creative—a prolific unpublished writer with many works she hoped to one day complete. She loved to read, play cards, and listen to old school blues and R&B music. She loved laughing and had unique perspectives that amazed everyone.

Her faith shaped every aspect of her life. TK was a persistent prayer warrior who would stop mid-conversation to pray for someone who came to mind. She prayed for the same people multiple times a day and asked about them daily until the prayer was answered. Her spiritual gift was her loving heart—always worried about other people's hearts.

Everyone felt fortunate to be around TK. She left an impression on people and was rarely forgotten, even by those who only met her once. Her lesson to all was simple: always tell people you love them. Everyone knew where they stood with her—she never held back her feelings.

On March 15, 2026, the sun set on a life beautifully lived. Though she endured illness with unwavering faith, supported by Randall's constant devotion, her legacy of love, creativity, and persistent prayer remains a guiding light for all who knew her.`,
  randallsWords: "I love her more than air. I can't wait to see her again.",
  herMessage: "Love Jesus. He loves you anyway but He can't make you love Him.",
  scriptures: [
    { verse: "God is our refuge and strength, A very present help in trouble.", reference: "Psalm 46:1 (NKJV)", note: "Her favorite scripture" },
    { verse: "Strength and honor are her clothing; She shall rejoice in time to come.", reference: "Proverbs 31:25 (NKJV)" },
    { verse: "The Lord is near to those who have a broken heart, And saves such as have a contrite spirit.", reference: "Psalm 34:18 (NKJV)" },
    { verse: "Precious in the sight of the Lord Is the death of His saints.", reference: "Psalm 116:15 (NKJV)" },
    { verse: "And God will wipe away every tear from their eyes; there shall be no more death, nor sorrow, nor crying.", reference: "Revelation 21:4 (NKJV)" },
    { verse: "I am the resurrection and the life. He who believes in Me, though he may die, he shall live.", reference: "John 11:25 (NKJV)" }
  ],
  orderOfService: [
    { item: "Musical Prelude", by: "Recorded Selections" },
    { item: "The Processional", by: "Clergy and Family" },
    { item: "Opening Hymn", by: '"Great Is Thy Faithfulness"' },
    { item: "Prayer of Comfort", by: "David M. Rhodes - Pastor" },
    { item: "Scripture Readings (NKJV)", by: "Psalm 46:1 | John 11:25" },
    { item: "Musical Selection", by: "Recorded Selection" },
    { item: "Life Reflection", by: "Read Silently" },
    { item: "Tributes & Remarks", by: "2 Minutes, Please" },
    { item: "Words of Encouragement", by: "Poem Read" },
    { item: "Musical Selection", by: "Recorded Selection" },
    { item: "The Eulogy", by: "David M. Rhodes - Pastor" },
    { item: "The Benediction", by: "David M. Rhodes - Pastor" },
    { item: "The Recessional", by: '"I Need Thee Every Hour"' }
  ],
  photos: [
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/pp1ydskc_20260316_133118-IMG_STYLE.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/do24rw1y_FB_IMG_1773682101931.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/gwc5al6q_FB_IMG_1773975265975.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/p5gnh0ys_FB_IMG_1773975233632.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/tjb0z1vt_FB_IMG_1773975185277.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/ejafbajv_FB_IMG_1773975200042.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/j0x250y8_IMG_20260318_165324%20%282%29.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/29x3o5xr_IMG_20260318_165324.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/i9btxdui_IMG_20260318_165324%20%281%29.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/4efmv90d_FB_IMG_1773975259560.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/9fbern80_FB_IMG_1773975251733.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/7w0nmvsc_FB_IMG_1773975227362.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/tkwlpwx7_FB_IMG_1773975217695.jpg",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/781fq4dw_IMG_20260318_165325.png",
    "https://customer-assets.emergentagent.com/job_shades-of-blue/artifacts/u3pgpijt_IMG_20260318_165325%20%282%29.jpg"
  ]
};

// Audio player - user can upload their own music or use browser's built-in audio
// Note: For the actual service, please download and host your preferred instrumental locally
const AUDIO_URL = null; // Set to local audio file path when available

// Hero Section Component
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="hero-section relative min-h-screen flex items-center justify-content-center"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1596396825803-0a98bc796e73?w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="hero-overlay absolute inset-0 bg-gradient-to-b from-sky-900/40 via-sky-900/60 to-sky-900/80" />
      
      <div className="relative z-10 text-center px-4 py-20 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <p className="text-amber-200 font-body text-lg mb-4 tracking-widest uppercase">
            Celebrating the Life of
          </p>
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-bold italic mb-6 drop-shadow-lg"
          data-testid="hero-name"
        >
          {memorialData.name}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="text-white/90 font-body text-2xl sm:text-3xl mb-8"
          data-testid="hero-dates"
        >
          {memorialData.dates}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.8 }}
          className="divider-gold w-48 mx-auto mb-8"
        />
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="scripture-overlay max-w-xl mx-auto p-6"
        >
          <p className="font-scripture text-xl italic text-slate-700 mb-2">
            "{memorialData.scriptures[0].verse}"
          </p>
          <p className="text-amber-700 font-body text-sm">
            — {memorialData.scriptures[0].reference}
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12"
        >
          <a 
            href="#slideshow"
            className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full transition-all duration-300 backdrop-blur-sm"
          >
            <Play size={20} />
            <span className="font-body">View Memorial Slideshow</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Photo Slideshow Component
const PhotoSlideshow = ({ onEnterPresenter }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [currentScripture, setCurrentScripture] = useState(0);
  const intervalRef = useRef(null);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCurrentScripture(emblaApi.selectedScrollSnap() % memorialData.scriptures.length);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (isPlaying && emblaApi) {
      intervalRef.current = setInterval(() => {
        emblaApi.scrollNext();
      }, 8000); // 8 seconds per photo - loops continuously for 1 hour
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, emblaApi]);

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
    if (!isPlaying) {
      emblaApi && emblaApi.scrollNext();
    }
  };

  return (
    <section 
      id="slideshow" 
      data-testid="slideshow-section"
      className="py-24 px-4 bg-gradient-to-b from-sky-100 via-sky-50 to-white"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic mb-4">
            Cherished Memories
          </h2>
          <div className="divider-gold w-32 mx-auto" />
        </motion.div>

        {/* Main Slideshow */}
        <div className="relative">
          <div className="embla overflow-hidden rounded-2xl photo-frame" ref={emblaRef}>
            <div className="embla__container flex">
              {memorialData.photos.map((photo, index) => (
                <div key={index} className="embla__slide flex-shrink-0 w-full">
                  <div className="aspect-video relative bg-slate-100 flex items-center justify-center">
                    <img 
                      src={photo} 
                      alt={`Memory ${index + 1}`}
                      className={`max-w-full max-h-full ${(index === 1 || index === 2 || index === 3 || index === 5 || index === 8 || index === 11 || index === 12 || index === 13) ? 'object-contain' : 'object-cover w-full h-full'}`}
                      data-testid={`slideshow-image-${index}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 slideshow-btn"
            data-testid="slideshow-prev"
            aria-label="Previous photo"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 slideshow-btn"
            data-testid="slideshow-next"
            aria-label="Next photo"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Controls */}
        <div className="slideshow-controls mt-6">
          <button
            onClick={togglePlayPause}
            className={`slideshow-btn ${isPlaying ? 'bg-sky-500 text-white border-sky-500' : ''}`}
            data-testid="slideshow-play-pause"
            aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
          >
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          
          {/* Dots */}
          <div className="flex gap-2">
            {memorialData.photos.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                className={`carousel-dot ${selectedIndex === index ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {onEnterPresenter && (
            <button
              onClick={onEnterPresenter}
              className="slideshow-btn presenter-mode-btn"
              data-testid="enter-presenter-mode-btn"
              aria-label="Enter presenter mode"
              title="Full-screen Presenter Mode"
            >
              <Maximize size={20} />
            </button>
          )}
        </div>

        {/* Scripture Overlay */}
        <motion.div
          key={currentScripture}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="scripture-overlay max-w-2xl mx-auto mt-8 p-6 text-center bg-gradient-to-br from-white/95 to-sky-50/95 border-sky-200"
        >
          <p className="font-scripture text-xl italic text-slate-700 mb-2">
            "{memorialData.scriptures[currentScripture].verse}"
          </p>
          <p className="text-amber-700 font-body text-sm">
            — {memorialData.scriptures[currentScripture].reference}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Life Reflection Section
const LifeReflectionSection = () => {
  return (
    <section 
      data-testid="life-reflection-section"
      className="py-24 px-4 bg-gradient-to-b from-white via-sky-50/50 to-sky-100/30"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Feather className="text-amber-600" size={28} />
            <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic">
              Life Reflection
            </h2>
          </div>
          <div className="divider-gold w-32 mx-auto" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card className="border-sky-200 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-8 sm:p-12">
              {memorialData.lifeReflection.split('\n\n').map((paragraph, index) => (
                <p 
                  key={index} 
                  className="font-body text-slate-700 text-lg leading-relaxed mb-6 last:mb-0"
                  data-testid={`reflection-paragraph-${index}`}
                >
                  {paragraph}
                </p>
              ))}
            </CardContent>
          </Card>
        </motion.div>

        {/* Survivors */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <Users className="text-amber-600" size={24} />
            <h3 className="font-heading text-2xl text-sky-900 font-bold italic">
              Survivors
            </h3>
          </div>
          
          <Card className="border-sky-200 shadow-lg bg-gradient-to-br from-white to-sky-50">
            <CardContent className="p-6 text-center">
              <p className="font-body text-slate-700 text-lg mb-4" data-testid="survivor-husband">
                <span className="font-semibold">Devoted Husband:</span> {memorialData.family.husband}
              </p>
              <Separator className="my-4 bg-sky-100" />
              <p className="font-body text-slate-700 text-lg mb-2" data-testid="survivor-mother">
                <span className="font-semibold">Mother:</span> {memorialData.family.mother}
              </p>
              <Separator className="my-4 bg-sky-100" />
              <p className="font-body text-slate-700 text-lg mb-2">
                <span className="font-semibold">Sisters & Brothers:</span>
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-4" data-testid="survivor-siblings">
                {memorialData.family.siblings.map((sibling, index) => (
                  <span 
                    key={index}
                    className="bg-sky-100 text-sky-800 px-4 py-2 rounded-full font-body"
                  >
                    {sibling}
                  </span>
                ))}
              </div>
              <Separator className="my-4 bg-sky-100" />
              <p className="font-body text-slate-700 text-lg mb-2">
                <span className="font-semibold">Aunts:</span>
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-4" data-testid="survivor-aunts">
                {memorialData.family.aunts.map((aunt, index) => (
                  <span 
                    key={index}
                    className="bg-amber-50 text-amber-800 px-4 py-2 rounded-full font-body"
                  >
                    {aunt}
                  </span>
                ))}
              </div>
              <p className="font-body text-slate-600 mt-4 italic" data-testid="survivor-other">
                {memorialData.family.other}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// Order of Service Section
const OrderOfServiceSection = () => {
  return (
    <section 
      data-testid="order-of-service-section"
      className="py-24 px-4 bg-gradient-to-b from-sky-100 via-sky-50 to-sky-100/50"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <BookOpen className="text-amber-600" size={28} />
            <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic">
              Order of Service
            </h2>
          </div>
          <div className="divider-gold w-32 mx-auto" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card className="border-sky-200 shadow-xl bg-gradient-to-br from-white to-sky-50/80">
            <CardContent className="p-6 sm:p-10">
              {memorialData.orderOfService.map((item, index) => (
                <div 
                  key={index} 
                  className="service-item"
                  data-testid={`service-item-${index}`}
                >
                  <span className="font-body font-semibold text-sky-900">
                    {item.item}
                  </span>
                  <span className="service-dots" />
                  <span className="font-body text-slate-600 text-right">
                    {item.by}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// Service Details Section
const ServiceDetailsSection = () => {
  return (
    <section 
      data-testid="service-details-section"
      className="py-24 px-4 bg-sky-900 text-white"
    >
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-heading text-4xl sm:text-5xl font-bold italic mb-8">
            Memorial Service
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-8 mb-12">
            <div className="flex flex-col items-center">
              <div className="bg-white/10 rounded-full p-4 mb-4">
                <Calendar className="text-amber-300" size={32} />
              </div>
              <p className="font-body text-xl" data-testid="service-date">
                {memorialData.service.date}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Clock className="text-amber-300" size={18} />
                <p className="font-body text-lg text-white/80" data-testid="service-time">
                  {memorialData.service.time}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-white/10 rounded-full p-4 mb-4">
                <MapPin className="text-amber-300" size={32} />
              </div>
              <p className="font-body text-xl font-semibold" data-testid="service-location">
                {memorialData.service.location}
              </p>
              <p className="font-body text-white/80" data-testid="service-address">
                {memorialData.service.address}
              </p>
            </div>
          </div>
          
          <div className="bg-white/10 rounded-2xl p-6 max-w-md mx-auto">
            <p className="font-body text-amber-200 mb-2">Attire Request</p>
            <p className="font-heading text-2xl italic" data-testid="service-attire">
              Please wear {memorialData.service.attire} in her honor
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Download Program Section
const DownloadProgramSection = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/memorial/program/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Telma_TK_Linn_Memorial_Program.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section 
      data-testid="download-section"
      className="py-24 px-4 bg-gradient-to-b from-sky-100/50 via-sky-50 to-sky-100"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic mb-4">
            Memorial Program
          </h2>
          <div className="divider-gold w-32 mx-auto mb-6" />
          <p className="font-body text-slate-600 text-lg">
            Download the complete memorial program as a printable PDF
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex justify-center"
        >
          <Card className="program-preview max-w-md w-full border-sky-200 bg-gradient-to-br from-white to-sky-50">
            <CardContent className="p-8 text-center">
              <div className="bg-gradient-to-b from-sky-200 to-sky-100 rounded-lg p-8 mb-6">
                <p className="text-amber-700 font-body text-sm uppercase tracking-wider mb-2">
                  A Celebration of Life
                </p>
                <h3 className="font-heading text-2xl text-sky-900 font-bold italic mb-2">
                  {memorialData.name}
                </h3>
                <p className="font-body text-slate-600">
                  {memorialData.dates}
                </p>
              </div>
              
              <Button
                onClick={handleDownload}
                disabled={isDownloading}
                className="bg-sky-900 hover:bg-sky-800 text-white font-body px-8 py-6 text-lg rounded-full w-full"
                data-testid="download-pdf-btn"
              >
                {isDownloading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Preparing Download...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Download size={20} />
                    Download Program (PDF)
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// Memorial Music Section
const MemorialMusicSection = () => {
  const [currentTrack, setCurrentTrack] = useState(0);
  
  // YouTube video IDs from the playlist - using embeddable versions
  const tracks = [
    { id: "XisL2Iayhx0", title: "Goodness of God (Live)", artist: "CeCe Winans", duration: "4:35" },
    { id: "sIaT8Jl2zpI", title: "You Say", artist: "Lauren Daigle", duration: "4:35" },
    { id: "dlgLJEHF3Ok", title: "Change Me", artist: "Tamela Mann", duration: "6:07" },
    { id: "JfpvpgaArTY", title: "Great Is Thy Faithfulness", artist: "Carrie Underwood ft. CeCe Winans", duration: "4:21" },
    { id: "pVLqWYs1t4o", title: "His Eye Is On The Sparrow", artist: "Lauryn Hill & Tanya Blount", duration: "5:04" },
    { id: "A1pj_i34mXM", title: "Let Go, Let God", artist: "Xania Monet", duration: "3:55" },
    { id: "gLyJpyQrXPY", title: "I Need Thee Every Hour", artist: "Piano Hymns", duration: "3:20" },
    { id: "RPndg9V_O_Y", title: "When I Get On My Knees", artist: "Jelly Roll ft. P!NK", duration: "4:37" },
    { id: "WR6wi1rJAYk", title: "The Lord's Prayer", artist: "Jelly Roll ft. Lady Gaga", duration: "4:06" },
  ];
  
  return (
    <section 
      data-testid="memorial-music-section"
      className="py-24 px-4 bg-gradient-to-b from-sky-100 via-sky-50 to-white"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Music className="text-amber-600" size={28} />
            <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic">
              Memorial Music
            </h2>
          </div>
          <div className="divider-gold w-32 mx-auto mb-6" />
          <p className="font-body text-slate-600 text-lg">
            Hymns of comfort and celebration
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Card className="w-full max-w-3xl mx-auto border-sky-200 shadow-xl bg-white overflow-hidden">
            <CardContent className="p-0">
              {/* Hidden YouTube Player - small size to minimize video visibility */}
              <div className="relative bg-sky-950 flex items-center justify-center" style={{ height: "80px" }}>
                <iframe
                  width="1"
                  height="1"
                  src={`https://www.youtube.com/embed/${tracks[currentTrack].id}?autoplay=0&loop=0&rel=0`}
                  title={tracks[currentTrack].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute opacity-0"
                  style={{ width: "1px", height: "1px" }}
                ></iframe>
                
                {/* Audio visualization style display */}
                <div className="flex items-center gap-4 px-6">
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div 
                        key={i} 
                        className="w-1 bg-sky-400 rounded-full animate-pulse"
                        style={{ 
                          height: `${20 + Math.random() * 20}px`,
                          animationDelay: `${i * 0.1}s`
                        }}
                      />
                    ))}
                  </div>
                  <div className="text-white text-center">
                    <p className="font-body text-lg font-semibold">{tracks[currentTrack].title}</p>
                    <p className="font-body text-sm text-sky-300">{tracks[currentTrack].artist}</p>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <div 
                        key={i} 
                        className="w-1 bg-sky-400 rounded-full animate-pulse"
                        style={{ 
                          height: `${20 + Math.random() * 20}px`,
                          animationDelay: `${i * 0.15}s`
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Actual playable embed */}
              <div className="aspect-video w-full bg-black">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${tracks[currentTrack].id}?rel=0`}
                  title={tracks[currentTrack].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              
              {/* Track List */}
              <div className="p-4 bg-gradient-to-b from-sky-50 to-white max-h-64 overflow-y-auto">
                <p className="font-body text-xs text-slate-500 uppercase tracking-wider mb-3 px-2">Playlist • 9 tracks</p>
                {tracks.map((track, index) => (
                  <button
                    key={track.id}
                    onClick={() => setCurrentTrack(index)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center justify-between ${
                      currentTrack === index 
                        ? 'bg-sky-100 text-sky-900' 
                        : 'hover:bg-sky-50 text-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        currentTrack === index ? 'bg-sky-500 text-white' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {currentTrack === index ? <Play size={12} /> : index + 1}
                      </span>
                      <div>
                        <p className="font-body font-medium text-sm">{track.title}</p>
                        <p className="font-body text-xs text-slate-500">{track.artist}</p>
                      </div>
                    </div>
                    <span className="font-body text-xs text-slate-400">{track.duration}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

// Share Memories Section
const ShareMemoriesSection = () => {
  const [memories, setMemories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    message: ''
  });

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    try {
      const response = await axios.get(`${API}/memories`);
      setMemories(response.data);
    } catch (error) {
      console.error('Error fetching memories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.message) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/memories`, formData);
      setSubmitted(true);
      setFormData({ name: '', relationship: '', message: '' });
      fetchMemories();
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Error posting memory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <section 
      data-testid="share-memories-section"
      className="py-24 px-4 bg-gradient-to-b from-white via-sky-50/50 to-sky-100/30"
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <MessageCircle className="text-amber-600" size={28} />
            <h2 className="font-heading text-4xl sm:text-5xl text-sky-900 font-bold italic">
              Share a Memory
            </h2>
          </div>
          <div className="divider-gold w-32 mx-auto mb-6" />
          <p className="font-body text-slate-600 text-lg">
            Leave a message to honor TK's memory
          </p>
        </motion.div>

        {/* Memory Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <Card className="border-sky-200 shadow-xl bg-white">
            <CardContent className="p-6 sm:p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <Heart className="text-sky-500 mx-auto mb-4" size={48} />
                  <p className="font-heading text-2xl text-sky-900 mb-2">Thank You</p>
                  <p className="font-body text-slate-600">Your memory has been shared.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="font-body text-sm text-slate-700 mb-2 block">
                        Your Name *
                      </label>
                      <Input
                        type="text"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="border-sky-200 focus:border-sky-400"
                        required
                        data-testid="memory-name-input"
                      />
                    </div>
                    <div>
                      <label className="font-body text-sm text-slate-700 mb-2 block">
                        Relationship to TK
                      </label>
                      <Input
                        type="text"
                        placeholder="e.g., Friend, Coworker, Family"
                        value={formData.relationship}
                        onChange={(e) => setFormData({...formData, relationship: e.target.value})}
                        className="border-sky-200 focus:border-sky-400"
                        data-testid="memory-relationship-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-body text-sm text-slate-700 mb-2 block">
                      Your Memory or Message *
                    </label>
                    <Textarea
                      placeholder="Share your favorite memory, a message of condolence, or what TK meant to you..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="border-sky-200 focus:border-sky-400 min-h-[120px]"
                      required
                      data-testid="memory-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting || !formData.name || !formData.message}
                    className="w-full sm:w-auto bg-sky-900 hover:bg-sky-800 text-white font-body px-8 py-3"
                    data-testid="memory-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        Sharing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send size={18} />
                        Share Memory
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Display Memories */}
        {memories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 className="font-heading text-2xl text-sky-900 text-center mb-8 italic">
              Memories Shared ({memories.length})
            </h3>
            <div className="space-y-6">
              {memories.map((memory, index) => (
                <motion.div
                  key={memory.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-sky-100 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-100 to-sky-200 flex items-center justify-center flex-shrink-0">
                          <User className="text-sky-600" size={24} />
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <p className="font-body font-semibold text-sky-900">
                              {memory.name}
                            </p>
                            {memory.relationship && (
                              <span className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded-full font-body">
                                {memory.relationship}
                              </span>
                            )}
                          </div>
                          <p className="font-body text-slate-700 leading-relaxed mb-3">
                            {memory.message}
                          </p>
                          <p className="font-body text-xs text-slate-400">
                            {formatDate(memory.timestamp)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

// Audio Player Component
const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const audioRef = useRef(null);

  const togglePlay = () => {
    if (!AUDIO_URL) {
      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 3000);
      return;
    }
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        setIsMuted(false);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="audio-controls" data-testid="audio-player">
      {AUDIO_URL && <audio ref={audioRef} src={AUDIO_URL} preload="auto" loop />}
      
      <div className="flex flex-col gap-2 items-end">
        {showMessage && (
          <div className="bg-white/95 text-sky-900 px-4 py-2 rounded-lg shadow-lg text-sm font-body max-w-[200px] text-center">
            Music available at the service
          </div>
        )}
        
        <button
          onClick={togglePlay}
          className={`audio-player-btn w-14 h-14 rounded-full bg-white shadow-lg flex items-center justify-center text-sky-900 border-2 border-sky-200 hover:border-sky-400 ${isPlaying ? 'playing' : ''}`}
          data-testid="audio-play-btn"
          aria-label={isPlaying ? "Pause music" : "Play music"}
        >
          {isPlaying ? <Pause size={24} /> : <Music size={24} />}
        </button>
        
        {isPlaying && AUDIO_URL && (
          <button
            onClick={toggleMute}
            className="audio-player-btn w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center text-sky-900 border border-sky-200"
            data-testid="audio-mute-btn"
            aria-label={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        )}
      </div>
    </div>
  );
};

// Footer Section
const FooterSection = () => {
  return (
    <footer 
      data-testid="footer-section"
      className="py-16 px-4 bg-sky-950 text-white/80"
    >
      <div className="max-w-4xl mx-auto text-center">
        {/* TK's Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="font-body text-amber-300 text-sm uppercase tracking-widest mb-4">
            Her Message to You
          </p>
          <p className="font-scripture text-2xl sm:text-3xl italic text-white mb-2" data-testid="tk-message">
            "{memorialData.herMessage}"
          </p>
          <p className="text-white/60 font-body text-sm">— TK</p>
        </motion.div>

        <div className="divider-gold w-32 mx-auto mb-12 opacity-50" />

        {/* Randall's Words */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12"
        >
          <p className="font-body text-sky-300 text-sm uppercase tracking-widest mb-4">
            From Her Husband
          </p>
          <p className="font-scripture text-xl sm:text-2xl italic text-white/90" data-testid="randall-words">
            "{memorialData.randallsWords}"
          </p>
          <p className="text-white/60 font-body text-sm mt-2">— Randall</p>
        </motion.div>

        <div className="divider-gold w-32 mx-auto mb-8 opacity-50" />

        <Heart className="text-amber-400 mx-auto mb-4" size={32} />
        <p className="font-heading text-2xl italic text-white mb-2">
          Forever Loved. Forever Remembered.
        </p>
        <p className="font-body text-sm text-white/60">
          {memorialData.name} • {memorialData.dates}
        </p>
      </div>
    </footer>
  );
};

// Presenter Mode Component - Full screen slideshow for display during the service
const PresenterMode = ({ onExit }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentScripture, setCurrentScripture] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimerRef = useRef(null);
  const photos = memorialData.photos;

  // Auto-advance photos
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => {
        const next = (prev + 1) % photos.length;
        setCurrentScripture(next % memorialData.scriptures.length);
        return next;
      });
    }, 8000);
    return () => clearInterval(timer);
  }, [isPaused, photos.length]);

  // Escape key to exit
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onExit();
      if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
      }
      if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => (prev + 1) % photos.length);
      }
      if (e.key === ' ') {
        e.preventDefault();
        setIsPaused((p) => !p);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExit, photos.length]);

  // Auto-hide controls after 3 seconds of no mouse movement
  useEffect(() => {
    const handleMove = () => {
      setShowControls(true);
      clearTimeout(controlsTimerRef.current);
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    };
    window.addEventListener('mousemove', handleMove);
    // Start the timer initially
    controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      clearTimeout(controlsTimerRef.current);
    };
  }, []);

  return (
    <div
      data-testid="presenter-mode"
      className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center"
      style={{ cursor: showControls ? 'default' : 'none' }}
    >
      {/* Name banner at top */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent pt-6 pb-12 text-center"
          >
            <p className="font-heading text-3xl sm:text-4xl text-white italic drop-shadow-lg">
              {memorialData.name}
            </p>
            <p className="font-body text-white/70 text-sm mt-1">
              {memorialData.dates}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Photo display */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          className="w-full h-full flex items-center justify-center p-4"
        >
          <img
            src={photos[currentIndex]}
            alt={`Memory ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
            data-testid={`presenter-image-${currentIndex}`}
          />
        </motion.div>
      </AnimatePresence>

      {/* Scripture overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/70 to-transparent pb-6 pt-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScripture}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6 }}
            className="text-center px-8 max-w-3xl mx-auto"
          >
            <p className="font-scripture text-xl sm:text-2xl italic text-white/90 mb-1 drop-shadow-md">
              "{memorialData.scriptures[currentScripture].verse}"
            </p>
            <p className="text-amber-300/80 font-body text-sm">
              — {memorialData.scriptures[currentScripture].reference}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Photo counter */}
        <div className="flex justify-center gap-1.5 mt-4">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i === currentIndex ? 'bg-white w-6' : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to photo ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Controls overlay */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-4 right-4 z-20 flex items-center gap-2"
          >
            <button
              onClick={() => setIsPaused((p) => !p)}
              className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center text-white transition-all"
              data-testid="presenter-pause-btn"
              aria-label={isPaused ? 'Resume slideshow' : 'Pause slideshow'}
            >
              {isPaused ? <Play size={18} /> : <Pause size={18} />}
            </button>
            <button
              onClick={onExit}
              className="w-10 h-10 rounded-full bg-white/15 hover:bg-red-500/60 backdrop-blur-md flex items-center justify-center text-white transition-all"
              data-testid="presenter-exit-btn"
              aria-label="Exit presenter mode"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Left/Right navigation zones */}
      <button
        onClick={() => setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length)}
        className="absolute left-0 top-0 bottom-0 w-24 z-10 flex items-center justify-start pl-4 opacity-0 hover:opacity-100 transition-opacity"
        aria-label="Previous photo"
      >
        <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white">
          <ChevronLeft size={22} />
        </div>
      </button>
      <button
        onClick={() => setCurrentIndex((prev) => (prev + 1) % photos.length)}
        className="absolute right-0 top-0 bottom-0 w-24 z-10 flex items-center justify-end pr-4 opacity-0 hover:opacity-100 transition-opacity"
        aria-label="Next photo"
      >
        <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white">
          <ChevronRight size={22} />
        </div>
      </button>
    </div>
  );
};

// Main Home Component
const Home = () => {
  const [presenterMode, setPresenterMode] = useState(false);

  useEffect(() => {
    // Verify API connection
    axios.get(`${API}/`)
      .then(res => console.log('API Connected:', res.data))
      .catch(err => console.error('API Error:', err));
  }, []);

  if (presenterMode) {
    return <PresenterMode onExit={() => setPresenterMode(false)} />;
  }

  return (
    <div data-testid="memorial-tribute-app">
      <HeroSection />
      <PhotoSlideshow onEnterPresenter={() => setPresenterMode(true)} />
      <LifeReflectionSection />
      <OrderOfServiceSection />
      <ServiceDetailsSection />
      <MemorialMusicSection />
      <ShareMemoriesSection />
      <DownloadProgramSection />
      <FooterSection />
      <AudioPlayer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
